import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, PenLine, FileText, CheckCircle2, AlertTriangle, UploadCloud, Building2 } from "lucide-react";

const Shell = ({ children }) => (
  <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
    <div className="w-full max-w-3xl">
      <div className="flex items-center gap-2 mb-4 justify-center">
        <div className="h-10 w-10 rounded-xl bg-sky-100 text-sky-700 flex items-center justify-center"><PenLine className="h-5 w-5" /></div>
        <div className="text-center">
          <p className="text-xs uppercase tracking-[0.2em] text-sky-500">Assietica Sign</p>
          <p className="font-heading font-semibold text-slate-800">Compilazione questionario</p>
        </div>
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8" data-testid="signform-card">{children}</div>
      <p className="text-center text-xs text-slate-400 mt-4">© Assietica — Compilazione e firma digitale</p>
    </div>
  </div>
);

export default function SignFormPublic() {
  const { token } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [values, setValues] = useState({});
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [uploaded, setUploaded] = useState({});
  const [uploadingKey, setUploadingKey] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/sign/public/forms/${token}`);
      setData(data);
      setValues(data.values || {});
      const up = {}; (data.uploaded || []).forEach((k) => { up[k] = true; });
      setUploaded(up);
      if (data.sign_token && data.status === "compilato") nav(`/sign/${data.sign_token}`);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Modulo non disponibile o scaduto");
    } finally { setLoading(false); }
  }, [token]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const setVal = (id, v) => setValues((p) => ({ ...p, [id]: v }));

  const [pivaBusy, setPivaBusy] = useState(false);
  const lookupPiva = async (piva, ragioneId, comuneId) => {
    if (!piva || !piva.trim()) { toast.error("Inserisci prima la Partita IVA"); return; }
    setPivaBusy(true);
    try {
      const { data: r } = await api.get(`/sign/public/forms/lookup-piva`, { params: { piva: piva.trim() } });
      if (!r.disponibile) { toast.error(r.motivo || "Dati camerali non disponibili"); return; }
      setValues((p) => {
        const n = { ...p };
        if (ragioneId && r.ragione_sociale) n[ragioneId] = r.ragione_sociale;
        if (comuneId && r.comune) n[comuneId] = r.comune;
        return n;
      });
      toast.success(`Compilato: ${r.ragione_sociale}${r.comune ? " · " + r.comune : ""}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Verifica non riuscita"); }
    finally { setPivaBusy(false); }
  };

  const uploadAttachment = async (key, file) => {
    if (!file) return;
    setUploadingKey(key); setErr("");
    try {
      const fd = new FormData();
      fd.append("key", key);
      fd.append("file", file);
      await api.post(`/sign/public/forms/${token}/attachments`, fd);
      setUploaded((p) => ({ ...p, [key]: true }));
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setUploadingKey(""); }
  };

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      await api.post(`/sign/public/forms/${token}/save`, { values });
      const { data: res } = await api.post(`/sign/public/forms/${token}/submit`, { values });
      nav(`/sign/${res.sign_token}`);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (loading) return <Shell><div className="py-10 flex items-center justify-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin mr-2" /> Caricamento…</div></Shell>;
  if (err && !data) return <Shell><div className="text-center py-6"><div className="h-14 w-14 rounded-full mx-auto flex items-center justify-center mb-4 bg-red-100 text-red-600"><AlertTriangle className="h-7 w-7" /></div><h2 className="font-heading text-xl font-semibold text-slate-800">Modulo non disponibile</h2><p className="text-slate-500 mt-2">{err}</p></div></Shell>;

  const fields = data.fields || [];
  const sections = [...new Set(fields.map((f) => f.section || "Dati"))];
  const _norm = (s) => (s || "").toLowerCase();
  const _findId = (keys) => (fields.find((f) => (!f.type || f.type === "text" || f.type === "number") && keys.some((k) => _norm(f.label).includes(k))) || {}).id;
  const pivaFieldId = _findId(["partita iva", "p. iva", "p.iva", "piva"]);
  const ragioneFieldId = _findId(["ragione sociale", "denominazione", "contraente", "azienda"]);
  const comuneFieldId = _findId(["comune"]);

  return (
    <Shell>
      <div className="space-y-6" data-testid="signform-form">
        <div>
          <p className="text-xs uppercase tracking-wide text-sky-600 font-semibold">{data.product || "Questionario"}</p>
          <h2 className="font-heading text-xl font-semibold text-slate-800">{data.name}</h2>
          <p className="text-sm text-slate-500 mt-1">Compila i campi richiesti. Al termine verrai indirizzato alla firma digitale con codice OTP e documento d'identità.</p>
        </div>

        {sections.map((sec) => (
          <div key={sec} className="rounded-xl border border-slate-200 p-4">
            <h3 className="text-sm font-semibold text-slate-700 mb-3">{sec}</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {fields.filter((f) => (f.section || "Dati") === sec).map((f) => (
                <div key={f.id} className={f.type === "multiline" ? "sm:col-span-2" : ""}>
                  {f.type === "single_choice" ? (
                    <div>
                      <label className="text-sm text-slate-600 mb-1 block">{f.label}{f.required && <span className="text-red-500"> *</span>}</label>
                      <div className="flex flex-col gap-1.5" data-testid={`signform-field-${f.id}`}>
                        {(f.options || []).map((o, oi) => (
                          <label key={oi} className="flex items-center gap-1.5 text-sm text-slate-700 cursor-pointer">
                            <input type="radio" name={f.id} checked={values[f.id] === o.label} onChange={() => setVal(f.id, o.label)} data-testid={`signform-field-${f.id}-opt${oi}`} /> {o.label}
                          </label>
                        ))}
                      </div>
                    </div>
                  ) : f.type === "yes_no" ? (
                    <div>
                      <label className="text-sm text-slate-600 mb-1 block">{f.label}{f.required && <span className="text-red-500"> *</span>}</label>
                      <div className="flex gap-4" data-testid={`signform-field-${f.id}`}>
                        <label className="flex items-center gap-1.5 text-sm text-slate-700 cursor-pointer">
                          <input type="radio" name={f.id} checked={values[f.id] === "si"} onChange={() => setVal(f.id, "si")} data-testid={`signform-field-${f.id}-si`} /> Sì
                        </label>
                        <label className="flex items-center gap-1.5 text-sm text-slate-700 cursor-pointer">
                          <input type="radio" name={f.id} checked={values[f.id] === "no"} onChange={() => setVal(f.id, "no")} data-testid={`signform-field-${f.id}-no`} /> No
                        </label>
                      </div>
                    </div>
                  ) : f.type === "checkbox" ? (
                    <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer mt-1">
                      <Checkbox checked={values[f.id] === true || values[f.id] === "true"} onCheckedChange={(v) => setVal(f.id, !!v)} data-testid={`signform-field-${f.id}`} />
                      <span>{f.label}{f.required && <span className="text-red-500"> *</span>}</span>
                    </label>
                  ) : (
                    <>
                      <label className="text-sm text-slate-600 mb-1 block">{f.label}{f.required && <span className="text-red-500"> *</span>}</label>
                      {f.type === "multiline" ? (
                        <Textarea rows={3} value={values[f.id] || ""} onChange={(e) => setVal(f.id, e.target.value)} data-testid={`signform-field-${f.id}`} />
                      ) : (
                        <Input type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"} value={values[f.id] || ""} onChange={(e) => setVal(f.id, e.target.value)} data-testid={`signform-field-${f.id}`} />
                      )}
                      {f.id === pivaFieldId && (ragioneFieldId || comuneFieldId) && (
                        <button type="button" onClick={() => lookupPiva(values[f.id], ragioneFieldId, comuneFieldId)} disabled={pivaBusy}
                          className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-medium text-sky-700 hover:text-sky-900 disabled:opacity-50" data-testid="signform-piva-lookup">
                          {pivaBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Building2 className="h-3.5 w-3.5" />} Verifica P.IVA e compila i dati impresa
                        </button>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        {(data.attachments || []).length > 0 && (
          <div className="rounded-xl border border-slate-200 p-4" data-testid="signform-attachments">
            <h3 className="text-sm font-semibold text-slate-700 mb-3">Documenti da allegare</h3>
            <div className="space-y-3">
              {data.attachments.map((a) => (
                <div key={a.key} className="flex items-center justify-between gap-3">
                  <span className="text-sm text-slate-600">{a.label}{a.required && <span className="text-red-500"> *</span>}</span>
                  {uploaded[a.key] ? (
                    <span className="inline-flex items-center gap-1.5 text-sm text-emerald-700" data-testid={`signform-att-done-${a.key}`}><CheckCircle2 className="h-4 w-4" /> Caricato</span>
                  ) : (
                    <label className="inline-flex items-center gap-1.5 text-sm text-sky-600 hover:text-sky-700 cursor-pointer" data-testid={`signform-att-${a.key}`}>
                      {uploadingKey === a.key ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />} Carica
                      <input type="file" className="hidden" onChange={(e) => uploadAttachment(a.key, e.target.files?.[0])} />
                    </label>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {err && <p className="text-sm text-red-600" data-testid="signform-error">{err}</p>}

        <Button onClick={submit} disabled={busy} className="w-full h-11 bg-sky-600 hover:bg-sky-700 text-white" data-testid="signform-submit">
          {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <FileText className="h-4 w-4 mr-1.5" />} Conferma e prosegui alla firma
        </Button>
        <p className="text-[11px] text-slate-400 text-center">I dati verranno stampati sul modello originale della Compagnia e sottoposti a firma. La firma OTP ha evidenze tecniche ma è una firma elettronica semplice: non è una firma elettronica avanzata (FEA) né qualificata (FEQ).</p>
      </div>
    </Shell>
  );
}
